import {
    AlertTriangle,
    ArrowRight,
    Calendar,
    ChevronLeft,
    Clock3,
    LoaderCircle,
    PencilLine,
    ShieldCheck,
    Sparkles,
    ToggleLeft,
    ToggleRight,
} from 'lucide-react'
import React, { useContext, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './EditLeavePolicyScreen.module.css'

const LEAVE_TYPE_META = {
    CL: { label: 'Casual Leave', hint: 'General planned leaves for personal needs.' },
    SL: { label: 'Sick Leave', hint: 'For illness and medical contingencies.' },
    PL: { label: 'Privilege / Earned Leave', hint: 'Usually monthly accrued and carry-forward eligible.' },
    CO: { label: 'Compensatory Off', hint: 'Granted for working on holidays/week-offs.' },
}

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const toNumber = (value, fallback = 0) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
}

const buildFormState = (policies) => {
    const state = {}
    ;(Array.isArray(policies) ? policies : []).forEach((policy) => {
        const type = String(policy?.leaveType || '').toUpperCase()
        if (!type) return

        state[type] = {
            annualEntitlement: toNumber(policy?.annualEntitlement, 0),
            accrualType: policy?.accrualType || 'UPFRONT',
            accrualPerMonth: toNumber(policy?.accrualPerMonth, 0),
            carryForwardAllowed: Number(policy?.carryForwardAllowed) === 1,
            maxCarryForwardDays: toNumber(policy?.maxCarryForwardDays, 0),
            encashmentAllowed: Number(policy?.encashmentAllowed) === 1,
            maxEncashmentDaysPerYear: toNumber(policy?.maxEncashmentDaysPerYear, 0),
            isPaidLeave: Number(policy?.isPaidLeave) === 1,
            effectiveFrom: '',
        }
    })
    return state
}

const EditLeavePolicyScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const currentPolicies = useMemo(() => {
        const fromState = location?.state?.currentPolicies
        return Array.isArray(fromState) ? fromState : []
    }, [location?.state?.currentPolicies])

    const initialSelectedTypes = useMemo(() => {
        const selectedTypeFromState = String(location?.state?.selectedLeaveType || '').toUpperCase()
        if (selectedTypeFromState) return [selectedTypeFromState]
        return []
    }, [location?.state?.selectedLeaveType])

    const [formState, setFormState] = useState(() => buildFormState(currentPolicies))
    const [selectedTypes, setSelectedTypes] = useState(initialSelectedTypes)
    const [errors, setErrors] = useState({})
    const [isSaving, setIsSaving] = useState(false)

    const policyMap = useMemo(() => {
        const map = {}
        currentPolicies.forEach((policy) => {
            const type = String(policy?.leaveType || '').toUpperCase()
            if (type) map[type] = policy
        })
        return map
    }, [currentPolicies])

    const availableTypes = useMemo(() => Object.keys(formState), [formState])

    const isTypeSelected = (type) => selectedTypes.includes(type)

    const toggleType = (type) => {
        setSelectedTypes((prev) => {
            if (prev.includes(type)) return prev.filter((item) => item !== type)
            return [...prev, type]
        })
        setErrors((prev) => {
            const next = { ...prev }
            Object.keys(next).forEach((key) => {
                if (key.endsWith(`_${type}`)) delete next[key]
            })
            return next
        })
    }

    const setField = (leaveType, field, value) => {
        setFormState((prev) => ({
            ...prev,
            [leaveType]: {
                ...prev[leaveType],
                [field]: value,
            },
        }))
        setErrors((prev) => {
            const next = { ...prev }
            delete next[`${field}_${leaveType}`]
            if (field === 'accrualType') delete next[`accrualPerMonth_${leaveType}`]
            return next
        })
    }

    const handleBack = () => {
        navigate(-1)
    }

    const updateLeavePolicy = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) {
            toast.error('Missing authentication or store information.')
            return
        }

        if (selectedTypes.length === 0) {
            toast.error('Select at least one leave type to update.')
            return
        }

        const newErrors = {}
        selectedTypes.forEach((type) => {
            const data = formState[type]
            if (!data?.effectiveFrom) {
                newErrors[`effectiveFrom_${type}`] = `Effective date is required for ${type}.`
            }
            if (data?.annualEntitlement < 0) {
                newErrors[`annualEntitlement_${type}`] = `${type}: annual entitlement cannot be negative.`
            }
            if (
                data?.accrualType === 'MONTHLY' &&
                Number(data?.annualEntitlement || 0) > 0 &&
                Number(data?.accrualPerMonth || 0) <= 0
            ) {
                newErrors[`accrualPerMonth_${type}`] = `${type}: accrualPerMonth required for MONTHLY accrual.`
            }
            if (data?.carryForwardAllowed && Number(data?.maxCarryForwardDays || 0) < 0) {
                newErrors[`maxCarryForwardDays_${type}`] = `${type}: max carry-forward days cannot be negative.`
            }
            if (data?.encashmentAllowed && Number(data?.maxEncashmentDaysPerYear || 0) < 0) {
                newErrors[`maxEncashmentDaysPerYear_${type}`] = `${type}: max encashment days cannot be negative.`
            }
        })

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            toast.error('Please fix highlighted fields before saving.')
            return
        }
        setErrors({})

        try {
            setIsSaving(true)

            const updates = selectedTypes.map((type) => ({
                leaveType: type,
                annualEntitlement: toNumber(formState[type]?.annualEntitlement, 0),
                accrualType: formState[type]?.accrualType || 'UPFRONT',
                accrualPerMonth: toNumber(formState[type]?.accrualPerMonth, 0),
                carryForwardAllowed: formState[type]?.carryForwardAllowed ? 1 : 0,
                maxCarryForwardDays: toNumber(formState[type]?.maxCarryForwardDays, 0),
                encashmentAllowed: formState[type]?.encashmentAllowed ? 1 : 0,
                maxEncashmentDaysPerYear: toNumber(formState[type]?.maxEncashmentDaysPerYear, 0),
                isPaidLeave: formState[type]?.isPaidLeave ? 1 : 0,
                effectiveFrom: formState[type]?.effectiveFrom,
            }))

            const response = await fetch(BASE_URL_API + 'updateStoreLeavePolicy', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    updates,
                }),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                const count = Number(respo?.response?.updatedCount || 0)
                toast.success(`${count} leave policy type(s) updated.`)
                navigate('/payroll/setup/leave-policy', {
                    replace: true,
                    state: { refreshLeavePolicies: true },
                })
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'VAL_001':
                case 'VAL_002':
                case 'VAL_003':
                case 'VAL_004':
                case 'VAL_005':
                    toast.error(respo?.message || 'Invalid update data.')
                    break
                case 'VAL_008':
                    toast.info(respo?.message || 'No changes detected.')
                    break
                case 'CONF_002':
                    toast.error(respo?.message || 'Policy not found for selected type.')
                    break
                case 'SYS_002':
                    toast.error(respo?.message || 'Update failed due to server error.')
                    break
                default:
                    toast.error(respo?.message || 'Update failed.')
                    break
            }
        } catch (error) {
            console.error('updateStoreLeavePolicy error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    if (!currentPolicies.length) {
        return (
            <div className={styles.page}>
                <div className={styles.header}>
                    <button className={styles.backBtn} onClick={handleBack} type="button">
                        <ChevronLeft size={18} />
                    </button>
                    <div className={styles.headerMeta}>
                        <h1 className={styles.headerTitle}>Edit Leave Policy</h1>
                        <p className={styles.headerSub}>No current policy data received. Please return and retry.</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={handleBack} type="button">
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <div className={styles.heroPill}>
                        <PencilLine size={12} />
                        Version Update
                    </div>
                    <h1 className={styles.headerTitle}>Edit Leave Policy</h1>
                    <p className={styles.headerSub}>Create a new policy version effective from selected date(s).</p>
                </div>
            </div>

            <div className={styles.noticeCard}>
                <AlertTriangle size={16} />
                <p>
                    Updating policy does not retroactively change existing staff balances. For current-year balance
                    corrections, use manual leave credit/debit.
                </p>
            </div>

            <div className={styles.sectionCard}>
                <div className={styles.sectionHead}>
                    <Sparkles size={14} />
                    SELECT LEAVE TYPES TO UPDATE
                </div>
                <div className={styles.typeSelectRow}>
                    {availableTypes.map((type) => (
                        <button
                            key={type}
                            type="button"
                            className={`${styles.typeChip} ${isTypeSelected(type) ? styles.typeChipActive : ''}`}
                            onClick={() => toggleType(type)}
                        >
                            {type}
                        </button>
                    ))}
                </div>
            </div>

            <div className={styles.formWrap}>
                {selectedTypes.map((type) => {
                    const data = formState[type]
                    const current = policyMap[type] || {}

                    return (
                        <div key={type} className={styles.sectionCard}>
                            <div className={styles.sectionHeadRow}>
                                <div>
                                    <h3 className={styles.sectionTitle}>{LEAVE_TYPE_META[type]?.label || type}</h3>
                                    <p className={styles.sectionDesc}>{LEAVE_TYPE_META[type]?.hint || ''}</p>
                                </div>
                                <span className={styles.leaveCodeBadge}>{type}</span>
                            </div>

                            <div className={styles.fieldGrid}>
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>Annual Entitlement</label>
                                    <input
                                        className={`${styles.textInput} ${
                                            errors[`annualEntitlement_${type}`] ? styles.inputError : ''
                                        }`}
                                        type="number"
                                        min="0"
                                        step="0.5"
                                        value={data?.annualEntitlement}
                                        onChange={(event) =>
                                            setField(type, 'annualEntitlement', toNumber(event.target.value, 0))
                                        }
                                    />
                                    {errors[`annualEntitlement_${type}`] && (
                                        <p className={styles.fieldError}>{errors[`annualEntitlement_${type}`]}</p>
                                    )}
                                </div>

                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>Accrual Type</label>
                                    <div className={styles.segmentBtnGroup}>
                                        <button
                                            type="button"
                                            className={`${styles.segmentBtn} ${
                                                data?.accrualType === 'UPFRONT' ? styles.segmentBtnActive : ''
                                            }`}
                                            onClick={() => setField(type, 'accrualType', 'UPFRONT')}
                                        >
                                            <Clock3 size={13} />
                                            Upfront
                                        </button>
                                        <button
                                            type="button"
                                            className={`${styles.segmentBtn} ${
                                                data?.accrualType === 'MONTHLY' ? styles.segmentBtnActive : ''
                                            }`}
                                            onClick={() => setField(type, 'accrualType', 'MONTHLY')}
                                        >
                                            <Calendar size={13} />
                                            Monthly
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {data?.accrualType === 'MONTHLY' && (
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>Accrual Per Month</label>
                                    <input
                                        className={`${styles.textInput} ${
                                            errors[`accrualPerMonth_${type}`] ? styles.inputError : ''
                                        }`}
                                        type="number"
                                        min="0"
                                        step="0.1"
                                        value={data?.accrualPerMonth}
                                        onChange={(event) =>
                                            setField(type, 'accrualPerMonth', toNumber(event.target.value, 0))
                                        }
                                    />
                                    {errors[`accrualPerMonth_${type}`] && (
                                        <p className={styles.fieldError}>{errors[`accrualPerMonth_${type}`]}</p>
                                    )}
                                </div>
                            )}

                            <div className={styles.toggleGrid}>
                                <ToggleRow
                                    label="Carry Forward Allowed"
                                    value={data?.carryForwardAllowed}
                                    onToggle={() =>
                                        setField(type, 'carryForwardAllowed', !Boolean(data?.carryForwardAllowed))
                                    }
                                />
                                <ToggleRow
                                    label="Encashment Allowed"
                                    value={data?.encashmentAllowed}
                                    onToggle={() =>
                                        setField(type, 'encashmentAllowed', !Boolean(data?.encashmentAllowed))
                                    }
                                />
                                <ToggleRow
                                    label="Paid Leave"
                                    value={data?.isPaidLeave}
                                    onToggle={() => setField(type, 'isPaidLeave', !Boolean(data?.isPaidLeave))}
                                />
                            </div>

                            {data?.carryForwardAllowed && (
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>Max Carry Forward Days</label>
                                    <input
                                        className={`${styles.textInput} ${
                                            errors[`maxCarryForwardDays_${type}`] ? styles.inputError : ''
                                        }`}
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={data?.maxCarryForwardDays}
                                        onChange={(event) =>
                                            setField(type, 'maxCarryForwardDays', toNumber(event.target.value, 0))
                                        }
                                    />
                                    {errors[`maxCarryForwardDays_${type}`] && (
                                        <p className={styles.fieldError}>{errors[`maxCarryForwardDays_${type}`]}</p>
                                    )}
                                </div>
                            )}

                            {data?.encashmentAllowed && (
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>Max Encashment Days / Year</label>
                                    <input
                                        className={`${styles.textInput} ${
                                            errors[`maxEncashmentDaysPerYear_${type}`] ? styles.inputError : ''
                                        }`}
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={data?.maxEncashmentDaysPerYear}
                                        onChange={(event) =>
                                            setField(
                                                type,
                                                'maxEncashmentDaysPerYear',
                                                toNumber(event.target.value, 0)
                                            )
                                        }
                                    />
                                    {errors[`maxEncashmentDaysPerYear_${type}`] && (
                                        <p className={styles.fieldError}>
                                            {errors[`maxEncashmentDaysPerYear_${type}`]}
                                        </p>
                                    )}
                                </div>
                            )}

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>
                                    New Effective From <span className={styles.required}>*</span>
                                </label>
                                <input
                                    className={`${styles.textInput} ${
                                        errors[`effectiveFrom_${type}`] ? styles.inputError : ''
                                    }`}
                                    type="date"
                                    value={data?.effectiveFrom}
                                    onChange={(event) => setField(type, 'effectiveFrom', event.target.value)}
                                />
                                {errors[`effectiveFrom_${type}`] && (
                                    <p className={styles.fieldError}>{errors[`effectiveFrom_${type}`]}</p>
                                )}
                                <div className={styles.helperBox}>
                                    <p>
                                        <strong>Current policy active from:</strong> {current?.effectiveFrom || '—'}
                                    </p>
                                    <p>New policy will take effect from the date you select.</p>
                                    <p>Tip: Use January 1 for annual entitlement changes.</p>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>

            <div className={styles.bottomActions}>
                <div className={styles.selectionPill}>
                    <ShieldCheck size={14} />
                    {selectedTypes.length} type(s) selected for update
                </div>
                <button className={styles.saveBtn} type="button" onClick={updateLeavePolicy} disabled={isSaving}>
                    {isSaving ? (
                        <>
                            <LoaderCircle size={16} className={styles.spinIcon} />
                            Saving...
                        </>
                    ) : (
                        <>
                            Save Policy Version
                            <ArrowRight size={15} />
                        </>
                    )}
                </button>
            </div>
        </div>
    )
}

const ToggleRow = ({ label, value, onToggle }) => (
    <button type="button" className={styles.toggleRow} onClick={onToggle}>
        <span>{label}</span>
        <span className={value ? styles.toggleOn : styles.toggleOff}>
            {value ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
            {value ? 'ON' : 'OFF'}
        </span>
    </button>
)

export default EditLeavePolicyScreen